import { createSelector } from '@ngrx/store';
import { List, Set } from 'immutable';
import { extend, identity, pick, pickBy } from 'lodash';
import {
  ALL_LIST_FILTER,
  DEFAULT_SEARCH_PARAMS,
  IEntityFilters,
  IError,
  ISearchParams,
  SEARCH_LIST_FILTER,
  SearchParamsEnum,
} from './entity-data.data';
import { IDispatchResponse } from '../../utils/helpers/redux.helper';
import { Highlight } from '../search-data/sub-models/highlight.model';
import {
  ActionsEnum,
  AddToSelectedList,
  ClearFilters,
  ClearList,
  FetchFilters,
  FetchList,
  FetchListError,
  FetchNextPage,
  RemoveFromSelectedList,
  ResetEditingEntity,
  ResetSelectedList,
  SaveEditingEntity,
  SaveEntityNetworkError,
  SaveEntityNetworkSuccess,
  SearchList,
  SetAllLists,
  SetDispatchedResponse,
  SetEditingEntity,
  SetFilters,
  SetFiltersFetched,
  SetListFetched,
  SetMasterError,
  SetRefreshingFilters,
  SetSearchParams,
  SetSelectedList,
  UpdateList,
} from './entity-data.actions';

// state interfaces
export interface IEntityList {
  entities: List<any>;
  highlights: List<Highlight>;
  searchParams: ISearchParams;
  fetched: boolean;
  filters: IEntityFilters;
  filtersFetched: boolean;
  refreshingFilters: boolean;
  error: IError;
}

export interface IFilteredLists {
  [filter: string]: IEntityList;
}

export interface IFilteredListsState {
  filteredLists: IFilteredLists;
  masterError: IError;
}

export interface IEntityDispatchResponse extends IDispatchResponse {
  entity?: any;
  error: IError;
}

export interface IEditingEntity {
  originalEntity: any;
  entity: any;
  changed: boolean;
  saved: boolean;
  saving: boolean;
}

export interface IEntityState extends IFilteredListsState {
  editingEntity: IEditingEntity;
  dispatchedResponse: IEntityDispatchResponse;
  selectedEntities: Set<any>;
}

// initial list state
export const initialListState: IEntityList = {
  entities: null,
  highlights: null,
  searchParams: DEFAULT_SEARCH_PARAMS,
  fetched: false,
  filters: null,
  filtersFetched: false,
  refreshingFilters: false,
  error: null,
};

// reducer map
export function entityReducerMap<T extends IEntityState>() {
  return {
    [ActionsEnum.FETCH_LIST]: function (state: T, { payload }: FetchList): T {
      const { listFilter, mergeEntities } = payload;
      const filteredList: IEntityList = getFilteredList<T>(state, listFilter);

      const newfilteredList = {
        ...filteredList,
        ...(!mergeEntities ? { entities: null } : {}),
        fetched: false,
        error: null,
      };

      return extend({}, state, {
        filteredLists: setFilteredList<T>(state, newfilteredList, listFilter),
      });
    },
    [ActionsEnum.SEARCH_LIST]: function (state: T, { payload }: SearchList): T {
      const { listFilter } = payload;
      const filteredList: IEntityList = getFilteredList<T>(state, listFilter);

      const newfilteredList = {
        ...filteredList,
        entities: null,
        fetched: false,
        error: null,
      };

      return extend({}, state, {
        filteredLists: setFilteredList<T>(state, newfilteredList, listFilter),
      });
    },
    [ActionsEnum.FETCH_NEXT_PAGE]: function (
      state: T,
      { payload }: FetchNextPage
    ): T {
      const { listFilter } = payload;
      const filteredList: IEntityList = getFilteredList<T>(state, listFilter);

      const newfilteredList = {
        ...filteredList,
        fetched: false,
        error: null,
      };

      return extend({}, state, {
        filteredLists: setFilteredList<T>(state, newfilteredList, listFilter),
      });
    },
    [ActionsEnum.UPDATE_LIST]: function (state: T, { payload }: UpdateList): T {
      const {
        entities,
        searchParams,
        comparatorFn,
        filterByFn,
        highlights,
        listFilter,
      } = payload;
      const filteredList: IEntityList = getFilteredList<T>(state, listFilter);

      let results = mergeNewEntities(
        filteredList.entities || List<any>([]),
        entities,
        List<any>([]),
        true
      );

      if (!!filterByFn) {
        results = results.filter(filterByFn) as List<any>;
      }

      if (!!comparatorFn) {
        results = results.sort(comparatorFn) as List<any>;
      }

      const newFilteredList = {
        ...filteredList,
        entities: results,
        highlights,
        searchParams: pickBy(searchParams, identity),
        fetched: true,
        error: null,
      };

      return extend({}, state, {
        filteredLists: setFilteredList<T>(state, newFilteredList, listFilter),
      });
    },
    [ActionsEnum.SET_ALL_LISTS]: function (
      state: T,
      { payload }: SetAllLists
    ): T {
      const { filteredLists } = payload;

      return extend({}, state, { filteredLists });
    },

    [ActionsEnum.CLEAR_LIST]: function (state: T, { payload }: ClearList): T {
      const { listFilter } = payload;
      const filteredList: IEntityList = getFilteredList<T>(state, listFilter);
      const newFilteredList = {
        ...filteredList,
        entities: null,
        highlights: null,
        searchParams: pick(filteredList.searchParams, [
          SearchParamsEnum.sort_by,
          SearchParamsEnum.sort_order,
        ]),
        error: null,
        fetched: false,
      };

      return extend({}, state, {
        filteredLists: setFilteredList<T>(state, newFilteredList, listFilter),
      });
    },
    [ActionsEnum.SET_LIST_FETCHED]: function (
      state: T,
      { payload }: SetListFetched
    ): T {
      const { listFilter, fetched } = payload;
      const filteredList: IEntityList = getFilteredList<T>(state, listFilter);

      const newfilteredList = {
        ...filteredList,
        fetched,
      };

      return extend({}, state, {
        filteredLists: setFilteredList<T>(state, newfilteredList, listFilter),
      });
    },
    [ActionsEnum.FETCH_LIST_ERROR]: function (
      state: T,
      { payload }: FetchListError
    ): T {
      const { listFilter, error, data } = payload;
      const filteredList: IEntityList = getFilteredList<T>(state, listFilter);
      const listError: IError = {
        error,
        data,
      };

      const newFilteredList = {
        ...filteredList,
        fetched: true,
        error: listError,
      };

      return extend({}, state, {
        filteredLists: setFilteredList<T>(state, newFilteredList, listFilter),
      });
    },
    [ActionsEnum.SET_SEARCH_PARAMS]: function (
      state: T,
      { payload }: SetSearchParams
    ): T {
      const { listFilter, searchParams } = payload;
      const filteredList: IEntityList = getFilteredList<T>(state, listFilter);
      const newFilteredList = {
        ...filteredList,
        searchParams: pickBy(searchParams, identity),
      };

      return extend({}, state, {
        filteredLists: setFilteredList<T>(state, newFilteredList, listFilter),
      });
    },
    [ActionsEnum.FETCH_FILTERS]: function (
      state: T,
      { payload }: FetchFilters
    ): T {
      const { listFilter, delay } = payload;
      const refreshingFilters = delay > 0 ? { refreshingFilters: true } : {};
      const filteredList: IEntityList = getFilteredList<T>(state, listFilter);

      const newfilteredList = {
        ...filteredList,
        ...refreshingFilters,
        filtersFetched: false,
      };

      return extend({}, state, {
        filteredLists: setFilteredList<T>(state, newfilteredList, listFilter),
      });
    },
    [ActionsEnum.CLEAR_FILTERS]: function (
      state: T,
      { payload }: ClearFilters
    ): T {
      const { listFilter } = payload;
      const filteredList: IEntityList = getFilteredList<T>(state, listFilter);

      const newfilteredList = {
        ...filteredList,
        filters: null,
        filtersFetched: false,
        refreshingFilters: false,
      };

      return extend({}, state, {
        filteredLists: setFilteredList<T>(state, newfilteredList, listFilter),
      });
    },
    [ActionsEnum.SET_FILTERS]: function (state: T, { payload }: SetFilters): T {
      const { listFilter, filters } = payload;
      const filteredList: IEntityList = getFilteredList<T>(state, listFilter);

      const newfilteredList = {
        ...filteredList,
        filtersFetched: true,
        filters,
      };

      return extend({}, state, {
        filteredLists: setFilteredList<T>(state, newfilteredList, listFilter),
      });
    },
    [ActionsEnum.SET_FILTERS_FETCHED]: function (
      state: T,
      { payload }: SetFiltersFetched
    ): T {
      const { listFilter, fetched } = payload;
      const filteredList: IEntityList = getFilteredList<T>(state, listFilter);

      const newfilteredList = {
        ...filteredList,
        filtersFetched: fetched,
      };

      return extend({}, state, {
        filteredLists: setFilteredList<T>(state, newfilteredList, listFilter),
      });
    },
    [ActionsEnum.SET_REFRESHING_FILTERS]: function (
      state: T,
      { payload }: SetRefreshingFilters
    ): T {
      const { listFilter, refreshingFilters } = payload;
      const filteredList: IEntityList = getFilteredList<T>(state, listFilter);

      const newfilteredList = {
        ...filteredList,
        refreshingFilters,
      };

      return extend({}, state, {
        filteredLists: setFilteredList<T>(state, newfilteredList, listFilter),
      });
    },
    [ActionsEnum.SET_EDITING_ENTITY]: function (
      state: T,
      { payload }: SetEditingEntity
    ): T {
      const { entity, setOriginalEntity } = payload;
      const editingEntity = !!state.editingEntity
        ? {
            entity,
            originalEntity: setOriginalEntity
              ? entity
              : state.editingEntity.originalEntity,
            changed: !setOriginalEntity,
            saved: false,
          }
        : {
            entity,
            originalEntity: entity,
            changed: false,
            saved: false,
            saving: false,
          };
      const newEditingEntity = {
        ...(state.editingEntity || {}),
        ...editingEntity,
      };

      return extend({}, state, {
        editingEntity: newEditingEntity,
      });
    },
    [ActionsEnum.RESET_EDITING_ENTITY]: function (
      state: T,
      { payload }: ResetEditingEntity
    ): T {
      return extend({}, state, {
        editingEntity: null,
      });
    },
    [ActionsEnum.SAVE_EDITING_ENTITY]: function (
      state: T,
      { payload }: SaveEditingEntity
    ): T {
      const newEditingEntity = {
        ...state.editingEntity,
        saving: true,
      };

      return extend({}, state, {
        editingEntity: newEditingEntity,
      });
    },
    [ActionsEnum.SAVE_ENTITY_NETWORK_SUCCESS]: function (
      state: T,
      { payload }: SaveEntityNetworkSuccess
    ): T {
      const { sourceAction } = payload;

      if (sourceAction === ActionsEnum.SAVE_EDITING_ENTITY) {
        const newEditingEntity = {
          ...state.editingEntity,
          saved: true,
          saving: false,
        };

        return extend({}, state, {
          editingEntity: newEditingEntity,
        });
      } else {
        return state;
      }
    },
    [ActionsEnum.SAVE_ENTITY_NETWORK_ERROR]: function (
      state: T,
      { payload }: SaveEntityNetworkError
    ): T {
      const { sourceAction } = payload;

      if (sourceAction === ActionsEnum.SAVE_EDITING_ENTITY) {
        const newEditingEntity = {
          ...state.editingEntity,
          saved: false,
          saving: false,
        };

        return extend({}, state, {
          editingEntity: newEditingEntity,
        });
      } else {
        return state;
      }
    },
    [ActionsEnum.ADD_TO_SELECTED_LIST]: function (
      state: T,
      { payload }: AddToSelectedList
    ): T {
      const { entity } = payload;
      const selectedEntities = state.selectedEntities || Set([]);
      const newSelectedEntities = selectedEntities.add(entity);

      return extend({}, state, {
        selectedEntities: newSelectedEntities,
      });
    },
    [ActionsEnum.REMOVE_FROM_SELECTED_LIST]: function (
      state: T,
      { payload }: RemoveFromSelectedList
    ): T {
      const { entity } = payload;
      const selectedEntities = state.selectedEntities || Set([]);
      const newSelectedEntities = selectedEntities.remove(entity);

      return extend({}, state, {
        selectedEntities: newSelectedEntities,
      });
    },
    [ActionsEnum.RESET_SELECTED_LIST]: function (
      state: T,
      { payload }: ResetSelectedList
    ): T {
      return extend({}, state, {
        selectedEntities: null,
      });
    },
    [ActionsEnum.SET_SELECTED_LIST]: function (
      state: T,
      { payload }: SetSelectedList
    ): T {
      const { entities } = payload;

      return extend({}, state, {
        selectedEntities: entities,
      });
    },
    [ActionsEnum.SET_DISPATCHED_RESPONSE]: function (
      state: T,
      { payload }: SetDispatchedResponse
    ): T {
      const { dispatchId, isSuccess, entity, error, data } = payload;
      const err: IError = {
        error,
        data,
      };
      const dispatchedResponse: IEntityDispatchResponse = {
        dispatchId,
        isSuccess,
        entity,
        error: err,
      };

      return extend({}, state, { dispatchedResponse });
    },
    [ActionsEnum.SET_MASTER_ERROR]: function (
      state: T,
      { payload }: SetMasterError
    ): T {
      const { error, data } = payload;
      const masterError: IError = {
        error,
        data,
      };

      return extend({}, state, { masterError });
    },
    [ActionsEnum.CLEAR_MASTER_ERROR]: function (state: T): T {
      return extend({}, state, { masterError: null });
    },
  };
}

// selector helpers
export function getAllLists<T extends IEntityState>(selectFeature) {
  return createSelector(selectFeature, (state: T) => state.filteredLists);
}

export function getEntitiesList<T extends IEntityState>(
  selectFeature,
  listFilter: string
) {
  return createSelector(
    selectFeature,
    (state: T) => getFilteredList<T>(state, listFilter).entities
  );
}

export function getHighlightsList<T extends IEntityState>(
  selectFeature,
  listFilter: string = SEARCH_LIST_FILTER
) {
  return createSelector(
    selectFeature,
    (state: T) => getFilteredList<T>(state, listFilter).highlights
  );
}

export function getSearchParams<T extends IEntityState>(
  selectFeature,
  listFilter: string
) {
  return createSelector(
    selectFeature,
    (state: T) => getFilteredList<T>(state, listFilter).searchParams
  );
}

export function getListError<T extends IEntityState>(
  selectFeature,
  listFilter: string
) {
  return createSelector(
    selectFeature,
    (state: T) => getFilteredList<T>(state, listFilter).error
  );
}

export function getListFetched<T extends IEntityState>(
  selectFeature,
  listFilter: string
) {
  return createSelector(
    selectFeature,
    (state: T) => getFilteredList<T>(state, listFilter).fetched
  );
}

export function getListFilters<T extends IEntityState>(
  selectFeature,
  listFilter: string
) {
  return createSelector(
    selectFeature,
    (state: T) => getFilteredList<T>(state, listFilter).filters
  );
}

export function getListFiltersFetched<T extends IEntityState>(
  selectFeature,
  listFilter: string
) {
  return createSelector(
    selectFeature,
    (state: T) => getFilteredList<T>(state, listFilter).filtersFetched
  );
}

export function getListRefreshingFilters<T extends IEntityState>(
  selectFeature,
  listFilter: string
) {
  return createSelector(
    selectFeature,
    (state: T) => getFilteredList<T>(state, listFilter).refreshingFilters
  );
}

export function getEditingEntity<T extends IEntityState>(selectFeature) {
  return createSelector(
    selectFeature,
    (state: T) => (state.editingEntity && state.editingEntity.entity) || null
  );
}

export function getOriginalEditingEntity<T extends IEntityState>(
  selectFeature
) {
  return createSelector(
    selectFeature,
    (state: T) =>
      (state.editingEntity && state.editingEntity.originalEntity) || null
  );
}

export function getEditingEntityChanged<T extends IEntityState>(selectFeature) {
  return createSelector(
    selectFeature,
    (state: T) => state.editingEntity && state.editingEntity.changed
  );
}

export function getEditingEntitySaved<T extends IEntityState>(selectFeature) {
  return createSelector(
    selectFeature,
    (state: T) => state.editingEntity && state.editingEntity.saved
  );
}

export function isSavingEditingEntity<T extends IEntityState>(selectFeature) {
  return createSelector(
    selectFeature,
    (state: T) => state.editingEntity && state.editingEntity.saving
  );
}

export function getSelectedEntities<T extends IEntityState>(selectFeature) {
  return createSelector(selectFeature, (state: T) => state.selectedEntities);
}

export function getMasterError<T extends IEntityState>(selectFeature) {
  return createSelector(selectFeature, (state: T) => state.masterError);
}

export function getDispatchedResponse<T extends IEntityState>(selectFeature) {
  return createSelector(selectFeature, (state: T) => state.dispatchedResponse);
}

function getFilteredList<T extends IEntityState>(
  state: T,
  listFilter = ALL_LIST_FILTER
) {
  return state.filteredLists[listFilter];
}

function setFilteredList<T extends IEntityState>(
  state: T,
  newList: IEntityList,
  listFilter = ALL_LIST_FILTER
) {
  return {
    ...state.filteredLists,
    [listFilter]: newList,
  };
}

// entity helpers
export function mergeNewEntities(
  currentEntities: List<any> = List([]),
  newEntities: List<any> = List([]),
  deletedEntityIds: List<string> = List([]),
  appendNewEntities: boolean = false,
  idField: string = 'id'
): List<any> {
  const uniqueIds = {};
  const entitiesWithRemovals: List<any> = currentEntities
    .filterNot((entity) =>
      deletedEntityIds.some(
        (deletedEntityId: string) =>
          entity && entity[idField] === deletedEntityId
      )
    )
    .toList();
  const concatEntities = appendNewEntities
    ? entitiesWithRemovals.concat(newEntities)
    : newEntities.concat(entitiesWithRemovals);

  return concatEntities.filter((entity: any) => {
    const entityId = entity[idField];

    if (!entityId || uniqueIds[entityId]) {
      return false;
    } else {
      uniqueIds[entityId] = true;

      return true;
    }
  }) as List<any>;
}
